import { getEwsRequestString, OwaDate } from 'owa-datetime';
import FreeBusyViewType from 'owa-service/lib/contract/FreeBusyViewType';
import GetUserAvailabilityInternalResponse from 'owa-service/lib/contract/GetUserAvailabilityInternalResponse';
import duration from 'owa-service/lib/factory/duration';
import emailAddress from 'owa-service/lib/factory/emailAddress';
import freeBusyViewOptions from 'owa-service/lib/factory/freeBusyViewOptions';
import getUserAvailabilityRequest from 'owa-service/lib/factory/getUserAvailabilityRequest';
import mailboxData from 'owa-service/lib/factory/mailboxData';
import getUserAvailabilityInternalOperation from 'owa-service/lib/operation/getUserAvailabilityInternalOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { lazyLogServiceCallForDiagnostics } from 'owa-calendar-forms-diagnostics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { CalendarId } from 'owa-calendar-cache';
import { getDefaultRequestOptions } from 'owa-headers';

/**
 * Makes the GetUserAvailabilityInternal call to the server to get calendar items for linked calendar
 * @param {OwaDate} rangeStart start of request range
 * @param {OwaDate} rangeEnd end of request range
 * @param {string[]} emails smtp address array of request mailbox
 * @param {FreeBusyViewType} requestedView FreeBusyViewType requested
 * @returns promise of calendar items
 */
export async function getUserAvailabilityService(
    rangeStart: OwaDate,
    rangeEnd: OwaDate,
    emails: string[],
    requestedView: FreeBusyViewType,
    timeZone?: string,
    calendarId?: CalendarId
): Promise<GetUserAvailabilityInternalResponse> {
    const header = getJsonRequestHeader();
    if (timeZone) {
        header.TimeZoneContext.TimeZoneDefinition.Id = timeZone;
    }
    let response;

    let options = null;
    if (calendarId) {
        options = getDefaultRequestOptions(calendarId.mailboxInfo);
    }

    try {
        response = await getUserAvailabilityInternalOperation(
            {
                request: {
                    Header: header,
                    Body: getUserAvailabilityRequest({
                        MailboxDataArray: emails.map(email =>
                            mailboxData({ Email: emailAddress({ Address: email }) })
                        ),
                        FreeBusyViewOptions: freeBusyViewOptions({
                            RequestedView: requestedView,
                            TimeWindow: duration({
                                StartTime: getEwsRequestString(rangeStart),
                                EndTime: getEwsRequestString(rangeEnd),
                            }),
                        }),
                    }),
                },
            },
            options
        );
    } finally {
        if (isFeatureEnabled('cal-mf-diagnostics')) {
            lazyLogServiceCallForDiagnostics.import().then(logServiceCall =>
                logServiceCall({
                    action: 'GetUserAvailabilityInternal',
                    status: response ? response.Body.ResponseCode : 'Error',
                    response: response && response.Body,
                })
            );
        }
    }

    return response.Body;
}
